import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectPrimaryWheel } from "../redux/reducers/gallery";
import { getLatestWheelToShowInReferModal } from "../services/api";
import { getLoggedInUser } from "../utils/helpers";
import { selectAvatarWheelSegments } from "../utils/selectAvatarWheelSegments";

/**
 * The logged-in user's own header/nav avatar frame (ProfileMenu's
 * `wheelUpdatedDataSorted` prop) — same Primary-Wheel-first, refer-modal-fetch
 * fallback logic already proven in Wrapper/index.jsx and pages/Gallery/index.jsx
 * (ISP-9999). Every OTHER bare `<GalleryHeader />` call site (Chat/Messages,
 * Notifications, Capture, MediaLibrary, ExploreV2) never wired this prop at
 * all, so ProfileMenu's `wheelUpdatedDataSorted?.length > 0` check always fell
 * through to the hardcoded 6-segment `IYUGA_BRAND_WHEEL_COLORS` generic wheel
 * for every logged-in user, wheel or no wheel (BUG gordon-chat-avatar-default-
 * wheelframe, measured live 2026-08-31: Kelly/12208's real 3-segment wheel
 * GalleryHeader now calls this hook whenever its caller does NOT pass an
 * explicit `wheelUpdatedDataSorted` (Discover, Notifications, Capture, …).
 */
export default function useAccountAvatarWheelSegments({ enabled = true } = {}) {
  const primaryWheel = useSelector(selectPrimaryWheel);
  const [myWheelToRender, setMyWheelToRender] = useState(null);

  const myWheelUpdatedDataSorted = useMemo(() => {
    const segments = myWheelToRender?.wheel_segments?.length > 0
      ? myWheelToRender.wheel_segments.map((obj) => ({ ...obj }))
      : [];
    return segments.length > 0
      ? [...segments].sort((a, b) => a.position - b.position)
      : [];
  }, [myWheelToRender?.wheel_segments]);

  useEffect(() => {
    if (!enabled) {
      setMyWheelToRender(null);
      return;
    }

    const loggedInUserId = getLoggedInUser()?.id;
    if (!loggedInUserId) {
      setMyWheelToRender(null);
      return;
    }

    const fetchMyLatest = async () => {
      const response = await getLatestWheelToShowInReferModal(loggedInUserId);
      if (response?.data?.data?.id) {
        setMyWheelToRender(response.data.data);
        return;
      }
      setMyWheelToRender(null);
    };

    fetchMyLatest();
  }, [primaryWheel, enabled]);

  return useMemo(() => {
    if (!enabled) return null;
    return selectAvatarWheelSegments(
      primaryWheel?.wheel_segments,
      myWheelUpdatedDataSorted
    );
  }, [enabled, primaryWheel?.wheel_segments, myWheelUpdatedDataSorted]);
}
